import { LocaleCode } from '@choco-admin/types';
import { CountryCode } from '@choco-admin/utils';
import { Divider } from '@heroui/react';
import React from 'react';
import {
  Controller,
  Control,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';

import { CopyButton } from '../../../../../components/CopyButton';
import {
  Input as FormInput,
  PhoneInput,
  LocaleSelectField,
} from '../../../../../components/FormComponents';
import { useSsoRestriction } from '../../../contexts';
import { SsoRestrictionTooltip } from '../../SsoRestrictionTooltip';
import { UserFormData } from '../hooks/useUserFormValidation';

interface BasicInformationSectionProps {
  control: Control<UserFormData>;
  errors: FieldErrors<UserFormData>;
  countryCode: CountryCode;
  onChangeCountryCode: (newCountryCode: CountryCode) => void;
  setValue: UseFormSetValue<UserFormData>;
  isLoginEmailMethodSelected: boolean;
  isLoginPhoneMethodSelected: boolean;
  getInputSubLabel: (isRequired: boolean) => string;
  disabled?: boolean;
}

export const BasicInformationSection: React.FC<
  BasicInformationSectionProps
> = ({
  control,
  errors,
  countryCode,
  onChangeCountryCode,
  setValue,
  isLoginEmailMethodSelected,
  isLoginPhoneMethodSelected,
  getInputSubLabel,
  disabled = false,
}) => {
  const { isSsoUser } = useSsoRestriction();
  // Language and email are sourced from the identity provider, so they are
  // read-only for SSO users. Name and phone number are portal-owned contact
  // details and stay editable unless the form is fully disabled (e.g. a
  // deleted user).
  const identityDisabled = disabled || isSsoUser;
  const contactDisabled = disabled;

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center">
          <h3 className="text-lg font-medium text-foreground">
            Basic Information
          </h3>
          <SsoRestrictionTooltip />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Full Name */}
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <FormInput
                id="user-name"
                name={field.name}
                label="Full Name"
                value={field.value}
                placeholder="Enter full name"
                required={true}
                isInvalid={!!errors.name}
                errorMessage={errors.name?.message}
                onChange={field.onChange}
                disabled={contactDisabled}
              />
            )}
          />

          {/* Locale */}
          <Controller
            name="locale"
            control={control}
            render={({ field }) => (
              <LocaleSelectField
                value={field.value as LocaleCode}
                onChange={field.onChange}
                isRequired={true}
                errorMessage={errors.locale?.message}
                data-testid="user-locale-field"
                aria-label="User locale"
                isDisabled={identityDisabled}
              />
            )}
          />

          {/* Phone */}
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <PhoneInput
                id="user-phone"
                name={field.name}
                label="Phone"
                labelEndContent={
                  <CopyButton
                    name="phone number"
                    value={field.value}
                    testId="user-phone-copy"
                  />
                }
                value={field.value || ''}
                required={isLoginPhoneMethodSelected}
                isInvalid={!!errors.phone}
                errorMessage={errors.phone?.message}
                countryCode={countryCode}
                onChangeCountryCode={(newCountryCode) => {
                  onChangeCountryCode(newCountryCode);
                  setValue('countryCode', newCountryCode);
                }}
                onChange={(phoneNumber) => {
                  field.onChange(phoneNumber);
                }}
                placeholder="Enter phone number"
                helperText={getInputSubLabel(isLoginPhoneMethodSelected)}
                data-testid="user-phone-input"
                disabled={contactDisabled}
              />
            )}
          />

          {/* Email */}
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <FormInput
                id="user-email"
                name={field.name}
                label={
                  isLoginEmailMethodSelected
                    ? 'Login email'
                    : 'Contact email address'
                }
                labelEndContent={
                  <CopyButton
                    name={
                      isLoginEmailMethodSelected
                        ? 'login email'
                        : 'email address'
                    }
                    value={field.value}
                    testId="user-email-copy"
                  />
                }
                value={field.value || ''}
                placeholder="Enter email address"
                type="email"
                required={isLoginEmailMethodSelected}
                isInvalid={!!errors.email}
                errorMessage={errors.email?.message}
                onChange={field.onChange}
                disabled={identityDisabled}
                helperText={getInputSubLabel(isLoginEmailMethodSelected)}
                helperPlacement="above"
              />
            )}
          />
        </div>
      </div>

      <Divider />
    </>
  );
};
